import { ChangeDetectionStrategy, Component, HostBinding } from '@angular/core'
import { FooterNode, MbNavigationService, NavigationNode } from '../navigation/mb-navigation.service'
import { BehaviorSubject, combineLatest, Observable, of, Subscription } from 'rxjs'
import { MbCaasService } from '../services/mb-caas/mb-caas.service'
import { distinctUntilChanged, filter, map, shareReplay, switchMap, withLatestFrom } from 'rxjs/operators'
import { MediaQueries } from '../enums/media-queries'
import { CmsService, EventService, Page, Product, TranslationService } from '@spartacus/core'
import { ObjectUtils } from '../../shared/utils/object-utils'
import { CaasIdType } from '../enums/caas-id-types'
import { Key, Pagination } from '../services/mb-caas/mb-caas'
import { CmsLinkType } from '../enums/cms-link-type'
import { CmsConverterService } from '../services/cms-converter/cms-converter.service'
import { Image } from '../components/types'
import { FooterNavigationEvent } from '../components/shared/events/footer-navigation-event'
import { MbConstants } from '../../shared/mb-constants'
import { CountryService } from '../../site-context/facade/country.service'
import { CmsComponentUtils } from '../components/utils/cms-component-utils'
import { CurrentProductService } from '@spartacus/storefront'
import { environment } from '../../../../environments/environment'

@Component({
  selector: 'mb-footer',
  templateUrl: './footer.component.html',
  styleUrls: ['./footer.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class FooterComponent {

  @HostBinding('class') hostClasses = 'tw-z-[1]'

  cmsPage = new BehaviorSubject<Page>({})
  private subscription = new Subscription()

  page$ = this.cmsService.getCurrentPage().pipe(
    filter(page => !!page),
    distinctUntilChanged((p1, p2) => CmsComponentUtils.comparePages(p1, p2)),
    switchMap(
      page => page?.template === 'ProductDetailsPageTemplate' ? this.currentProductService.getProduct().pipe(
        switchMap(data => this.resolveCategory(data))) : of(page)))

  pageColor$ = this.page$.pipe(
    map(page => page?.page?.formData?.pt_pageMainColor?.value?.identifier ||
      page?.properties?.formData?.pt_pageMainColor?.value?.identifier || 'corporate-orange')
  )

  footer$ = this.navigationService.footer$.pipe(
    filter(footer => !!footer?.children?.length),
    switchMap(footer => this.loadPages(footer)),
    shareReplay({ bufferSize: 1, refCount: true }))

  main$ = this.footer$.pipe(map(footer => footer.children?.filter((folder: NavigationNode) => folder.label !== this.others) ?? []))
  other$ = this.footer$.pipe(map(footer => footer.children?.find((folder: NavigationNode) => folder.label === this.others) ?? {}))
  copyright$ = this.translationService.translate('shared.footerCopyright', { year: new Date().getFullYear() })

  state$ = combineLatest([this.pageColor$, this.main$, this.other$, this.copyright$]).pipe(
    map(([pageColor, main, other, copyright]) => ({ pageColor, main, other, copyright }))) as Observable<FooterState>

  constructor (private navigationService: MbNavigationService,
               private mbCaasService: MbCaasService,
               private translationService: TranslationService,
               private events: EventService,
               private cmsService: CmsService,
               private currentProductService: CurrentProductService,
               private countryService: CountryService) {
  }

  sendGTAEvent (link: NavigationNode) {
    this.events.dispatch(new FooterNavigationEvent(MbConstants.FOOTER_NAVI, link?.label))
  }

  private loadPages (footer: FooterNode): Observable<any> {
    if (!footer?.children) {
      return of(footer)
    }
    const pageIds = ObjectUtils.flatten(footer.children ?? [], 'children')
      .map(child => child.id)
    const keys = [
      { key: 'page.formData.pt_seoUrl.value', operator: 1 },
      { key: 'page.formData.pt_footer_icon.value.value.target', operator: 1 },
      { key: 'page.formData.pt_footer_link_type.value.identifier', operator: 1 },
      { key: 'displayName', operator: 1 }
    ] as Key[]
    const pagination = { page: 1, pageSize: pageIds.length * 2 } as Pagination
    return this.mbCaasService.getByIds(pageIds, CaasIdType.PAGEID, keys, pagination)
      .pipe(
        withLatestFrom(this.countryService.getActive()),
        switchMap(([pages, activeCountry]) => {
          const ids = pages
            .filter((page: any) => activeCountry !== MbConstants.FALLBACK_COUNTRY && !page?.page?.formData?.pt_footer_icon?.value?.length)
            .map((page: any) => page.identifier)
          if (ids.length < 1) {
            return of(pages)
          }
          return this.mbCaasService.loadFallbacks(ids, []).pipe(
            map(fallbacks => pages
              .map((page: any) => fallbacks
                .find(fallback => fallback.identifier === page.identifier) ?? page)))
        }),
        map((pages: FooterCaasResult[]) => {
          if (!footer.children) {
            return footer
          }
          footer.children = footer.children
            .filter(child => !!child?.children)
            .map(child => this.processFooterLinks(child, pages))
          return footer
        })
      )
  }

  resolveCategory (pdpPage?: Page | Product | null) {
    const categoryId = `${environment.occ.productCatalog}/${pdpPage?.categories?.[0]?.code}`
    this.subscription.add(this.mbCaasService.getByIds([categoryId], CaasIdType.CATEGORY)
      .pipe(filter(page => page?.[0]?.displayName !== this.cmsPage.value?.displayName))
      .subscribe(page => this.cmsPage.next(page?.[0] ?? {})))
    return this.cmsPage
  }

  private processFooterLinks (child: FooterNode, pages: FooterCaasResult[]): FooterNode {
    if (!child?.children) {
      return child
    }

    let imageGroup: FooterNode[] = []
    const processedGrandChildren = []

    child.children.forEach((grandChild: FooterNode) => {
      const processedGrandChild = this.addDataToFooterNode(grandChild, pages)

      if (processedGrandChild.image?.original) {
        imageGroup.push(processedGrandChild)
      } else {
        if (imageGroup.length > 0) {
          processedGrandChildren.push(imageGroup)
          imageGroup = []
        }
        processedGrandChildren.push(processedGrandChild)
      }
    })

    if (imageGroup.length > 0) {
      processedGrandChildren.push(imageGroup)
    }

    child.children = processedGrandChildren as any
    child.hasIconLinks = child.children?.some((grandChild: FooterNode) => Array.isArray(grandChild))
    return child
  }

  private addDataToFooterNode (footerNode: FooterNode, pages: FooterCaasResult[] = []): FooterNode {
    const page = pages.find(p => p?.identifier === footerNode.id)
    if (!page) {
      return footerNode
    }
    const isExternalUrl = footerNode.url?.indexOf('http') > -1 || footerNode.url?.indexOf('www') > -1
    const cmsLinkType = page.page?.formData?.pt_footer_link_type?.value?.identifier as CmsLinkType || (isExternalUrl ? CmsLinkType.URL
      : CmsLinkType.CONTENT_LINK)
    const image = CmsConverterService.mapImage(page.page?.formData?.pt_footer_icon, true)
    footerNode.image = {
      original: image.original,
      xl: image.original
    } as Image
    footerNode.cmsLinkType = cmsLinkType
    return footerNode
  }

  readonly mediaQuery = MediaQueries
  readonly cmsLinkType = CmsLinkType
  private readonly others = 'others'
}

export interface FooterState {
  pageColor: string;
  main: FooterNode[];
  other: FooterNode;
  copyright: string;
}

export interface FooterCaasResult {
  displayName: string;
  identifier: string;
  locale: {
    country: string;
  };
  page: {
    formData: {
      // eslint-disable-next-line @typescript-eslint/naming-convention
      pt_seoUrl: {
        value: string;
      };
      pt_footer_icon: {
        value: [
          {
            value: {
              target: {
                baseUrl: string;
              };
            };
          }
        ];
      };
      pt_footer_link_type: {
        value: {
          identifier: string;
        };
      };
    };
  };
}
